import { normalizeProctoringMessage, PROCTOR_STOPPED_MESSAGE } from './proctoringMessages.js';

export function isRemoteProctoringAvailable() {
  return typeof window !== 'undefined'
    && !!window.remoteProctoring
    && typeof window.remoteProctoring.remoteProctorInitV2 === 'function';
}

async function readResponseMessage(response) {
  try {
    const clone = response.clone();
    const body = await clone.json();
    return normalizeProctoringMessage(body);
  } catch {
    return '';
  }
}

function installProctoringFetchCapture() {
  if (typeof window === 'undefined' || typeof window.fetch !== 'function') {
    return { getMessage: () => '', restore: () => {} };
  }

  const originalFetch = window.fetch;
  let capturedMessage = '';
  window.fetch = async (...args) => {
    const response = await originalFetch.apply(window, args);
    const url = String(args[0]?.url ?? args[0] ?? '');
    if (/\/rpsessionservice\/standalone\/v2/i.test(url)) {
      const message = await readResponseMessage(response);
      if (message) capturedMessage = message;
      if (!response.ok && !capturedMessage) {
        capturedMessage = PROCTOR_STOPPED_MESSAGE;
      }
    }
    return response;
  };

  return {
    getMessage: () => capturedMessage,
    restore: () => {
      window.fetch = originalFetch;
    },
  };
}

function ensureRemoteProctoringInstance() {
  if (typeof window === 'undefined') return;
  if (!window.remoteProctoring && window.ManipalRemoteProctoring?.RemoteProctor) {
    window.remoteProctoring = new window.ManipalRemoteProctoring.RemoteProctor();
  }
}

async function waitForRemoteProctoring(timeoutMs = 5000) {
  const startedAt = Date.now();
  while (Date.now() - startedAt < timeoutMs) {
    ensureRemoteProctoringInstance();
    if (isRemoteProctoringAvailable()) return true;
    await new Promise((resolve) => setTimeout(resolve, 100));
  }
  return false;
}

export async function getRemoteProctoringStatus() {
  ensureRemoteProctoringInstance();
  if (!isRemoteProctoringAvailable()) return false;
  if (typeof window.remoteProctoring.getRpStatus !== 'function') {
    return !!window.proctorStarted;
  }
  try {
    return !!(await window.remoteProctoring.getRpStatus());
  } catch {
    return !!window.proctorStarted;
  }
}

let pendingStart = null;

async function initializeRemoteProctoring(token) {
  const available = await waitForRemoteProctoring();
  if (!available) {
    throw new Error('Remote proctoring is not available. Please reload and try again.');
  }

  localStorage.setItem('RPToken', token);
  if (window.proctorStarted) {
    window.proctorStarted = true;
    localStorage.setItem('RPStart', '1');
    return true;
  }

  const capture = installProctoringFetchCapture();
  try {
    await window.remoteProctoring.remoteProctorInitV2({ authToken: token });
    const capturedMessage = capture.getMessage();
    if (capturedMessage) {
      throw new Error(capturedMessage);
    }
  } catch (err) {
    throw new Error(normalizeProctoringMessage(err) || 'Could not start remote proctoring. Please try again.');
  } finally {
    capture.restore();
  }
  window.proctorStarted = true;
  localStorage.setItem('RPStart', '1');
  return true;
}

export async function startRemoteProctoring(token) {
  if (!token) throw new Error('Remote proctoring token is missing.');

  if (!pendingStart) pendingStart = initializeRemoteProctoring(token);
  const start = pendingStart;
  try {
    return await start;
  } finally {
    if (pendingStart === start) pendingStart = null;
  }
}

export async function stopRemoteProctoring() {
  if (typeof window === 'undefined' || !window.remoteProctoring) return;
  try {
    if (typeof window.remoteProctoring.stop === 'function') {
      window.remoteProctoring.stop();
    }
  } finally {
    window.proctorStarted = false;
    localStorage.removeItem('RPToken');
    localStorage.removeItem('RPStart');
    localStorage.removeItem('proctorCourseId');
    localStorage.removeItem('proctorList');
  }
}
